import { useDisclosure } from '@cardboard-ui/react';
import React from 'react';
import {
  RenameDialog,
  RenameHandler,
} from 'components/OptionsMenu/options/RenameOption/RenameDialog';
import { OptionsMenuItem } from 'components/OptionsMenu/OptionsMenuItem';
import { t } from '@lingui/macro';
import { EditIcon } from 'components/icons';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { RenameOption_node$key } from './__generated__/RenameOption_node.graphql';
import { RenameOption_renameFolderNode_Mutation } from './__generated__/RenameOption_renameFolderNode_Mutation.graphql';
import { RenameOption_updateVaultSettings_Mutation } from './__generated__/RenameOption_updateVaultSettings_Mutation.graphql';

const nodeFragment = graphql`
  fragment RenameOption_node on Node {
    id
    ... on FolderNodeInterface {
      __typename
      name
      canRename {
        value
      }
    }
    ... on Vault {
      __typename
      name
      canUpdate {
        value
      }
    }
  }
`;

const updateFolderNodeMutation = graphql`
  mutation RenameOption_renameFolderNode_Mutation(
    $folderNodeId: ID!
    $name: String!
  ) {
    updateFolderNode(input: { folderNodeId: $folderNodeId, name: $name }) {
      folderNodeUpdated {
        ... on FolderNodeInterface {
          name
        }
        ... on Node {
          id
        }
      }
    }
  }
`;

const updateVaultSettingsMutation = graphql`
  mutation RenameOption_updateVaultSettings_Mutation(
    $vaultId: ID!
    $name: String!
  ) {
    updateVaultSettings(input: { vaultId: $vaultId, name: $name }) {
      vault {
        ... on Vault {
          name
        }
        ... on Node {
          id
        }
      }
    }
  }
`;

interface RenameOptionProps {
  node: RenameOption_node$key;
}

export const RenameOption = ({ node }: RenameOptionProps) => {
  const { id, __typename, name, canRename, canUpdate } = useFragment(
    nodeFragment,
    node,
  );
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [renameFolderNode, isRenameLoading] =
    useMutation<RenameOption_renameFolderNode_Mutation>(
      updateFolderNodeMutation,
    );

  const [updateVaultSettings, isVaultRenameLoading] =
    useMutation<RenameOption_updateVaultSettings_Mutation>(
      updateVaultSettingsMutation,
    );

  const onRename: RenameHandler = (newName, onCompleted) => {
    if (__typename === 'Vault') {
      updateVaultSettings({
        variables: { vaultId: id, name: newName },
        onCompleted,
      });
    } else {
      renameFolderNode({
        variables: { folderNodeId: id, name: newName },
        onCompleted,
      });
    }
  };

  if (!canRename?.value && !canUpdate?.value) return null;

  return (
    <>
      <OptionsMenuItem
        icon={<EditIcon />}
        onClick={onOpen}
      >{t`Rename`}</OptionsMenuItem>
      {/*
        Don't pre-render the Rename modal.
        1. For performance.
        2. If a rename happens after this is rendered (via the overlay) then this RenameModal will not be updated with the default value.

        The above issue should not happen, but it does. This is an easy fix.
        It may be related to the internal use of useForm
      */}
      {isOpen && (
        <RenameDialog
          isOpen={true}
          onClose={onClose}
          currentName={name || ''}
          isRenaming={isRenameLoading || isVaultRenameLoading}
          onRename={onRename}
        />
      )}
    </>
  );
};
